import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Modal, Form, Row, Col, Dropdown } from 'react-bootstrap'
import Message from '../../shared_components/Message'
import Loader from '../../shared_components/Loader'

import {
  createJitaiMessage,
  updateJitaiMessage,
  getJitaiVariableList,
} from '../actions/jitaiActions'
import {
  JITAI_MESSAGE_CREATE_RESET,
  JITAI_MESSAGE_UPDATE_RESET,
} from '../constants/jitaiConstants'

const UpsertJitaiMessage = ({ studyId, jitaiMessageList, updatedJitaiMessage }) => {
  const [show, setShow] = useState(false)
  const [code, setCode] = useState('')
  const [message, setMessage] = useState('')
  const messageTextareaRef = useRef(null)

  const dispatch = useDispatch()

  const jitaiMessageCreate = useSelector((state) => state.jitaiMessageCreate)
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
  } = jitaiMessageCreate

  const jitaiMessageUpdate = useSelector((state) => state.jitaiMessageUpdate)
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = jitaiMessageUpdate

  const jitaiVariableList = useSelector((state) => state.jitaiVariableList)
  const {
    loading: loadingVariables,
    error: errorVariables,
    jitaiVariables,
  } = jitaiVariableList

  const isEditMode = !!updatedJitaiMessage

  useEffect(() => {
    if (isEditMode && updatedJitaiMessage) {
      setCode(updatedJitaiMessage.code)
      setMessage(updatedJitaiMessage.message)
    }
  }, [isEditMode, updatedJitaiMessage])

  useEffect(() => {
    if (successCreate || successUpdate) {
      setShow(false)
      setCode('')
      setMessage('')
      dispatch({ type: JITAI_MESSAGE_CREATE_RESET })
      dispatch({ type: JITAI_MESSAGE_UPDATE_RESET })
    }
  }, [successCreate, successUpdate, dispatch])

  useEffect(() => {
    if (show && studyId) {
      dispatch(getJitaiVariableList(studyId))
    }
  }, [show, studyId, dispatch])

  const handleClose = () => {
    setShow(false)
    setCode('')
    setMessage('')
  }

  const handleShow = () => {
    if (isEditMode && updatedJitaiMessage) {
      setCode(updatedJitaiMessage.code)
      setMessage(updatedJitaiMessage.message)
    }
    setShow(true)
  }

  const submitHandler = (e) => {
    e.preventDefault()
    if (isEditMode) {
      dispatch(updateJitaiMessage(studyId, updatedJitaiMessage._id, { code, message }))
    } else {
      dispatch(createJitaiMessage(studyId, { code, message }))
    }
  }

  const insertVariablePlaceholder = (variableCode) => {
    const textarea = messageTextareaRef.current
    const placeholder = `%${variableCode}%`
    
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newMessage = message.substring(0, start) + placeholder + message.substring(end)
      
      setMessage(newMessage)
      
      // Set cursor position after the inserted placeholder
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length)
      }, 0)
    } else {
      // If textarea is not focused, append at the end
      setMessage(prevMessage => prevMessage + placeholder)
    }
  }

  return (
    <>
      <Button variant='primary' className='btn-sm' onClick={handleShow}>
        {isEditMode ? 'Edit' : 'Add'}
      </Button>

      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit' : 'Add'} Jitai Message
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingCreate || loadingUpdate ? (
            <Loader />
          ) : (
            <Form onSubmit={submitHandler}>
              <Row>
                <Col md={12}>
                  <Form.Group controlId='code'>
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter code'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className='mt-3'>
                <Col md={12}>
                  <Form.Group controlId='message'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Form.Label>Message</Form.Label>
                      <Dropdown>
                        <Dropdown.Toggle 
                          variant="outline-secondary" 
                          size="sm"
                          disabled={loadingVariables || !jitaiVariables || jitaiVariables.length === 0}
                        >
                          {loadingVariables ? 'Loading...' : 'Insert Variable'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {jitaiVariables && jitaiVariables.length > 0 ? (
                            jitaiVariables.map((variable) => (
                              <Dropdown.Item
                                key={variable._id}
                                onClick={() => insertVariablePlaceholder(variable.code)}
                              >
                                <strong>{variable.code}</strong>
                                <br />
                                <small className="text-muted">{variable.definition}</small>
                              </Dropdown.Item>
                            ))
                          ) : (
                            <Dropdown.Item disabled>
                              No variables available
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                    <Form.Control
                      as='textarea'
                      rows={8}
                      placeholder='Enter message (use %variableCode% for dynamic content)'
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      ref={messageTextareaRef}
                      required
                    />
                    {errorVariables && (
                      <Form.Text className="text-danger">
                        Error loading variables: {errorVariables}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {errorCreate && <Message variant='danger'>{errorCreate}</Message>}
              {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}

              <div className='mt-3'>
                <Button 
                  type='submit' 
                  variant='primary' 
                  style={{ marginRight: '12px' }}
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button variant='secondary' onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default UpsertJitaiMessage 